import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { dohvatiMinuteNeaktivnosti } from "@/lib/dokumenti";
import {
  jeDodirniUredaj,
  odjaviBeaconom,
  pratiAktivnost,
  registrirajTab,
} from "@/lib/sesija";

/**
 * Until the server has been asked, and if it cannot be.
 *
 * The real number is AVM_SESSION_IDLE_MINUTES and it comes from `/features`,
 * so the warning and the expiry cannot drift apart. This is only what the
 * clock runs on before the answer arrives.
 */
const PRETPOSTAVLJENE_MINUTE = 30;

/** How long the warning stands before the session goes. */
const ODBROJAVANJE_S = 60;

const DOGADAJI_AKTIVNOSTI = [
  "click",
  "keydown",
  "scroll",
  "touchstart",
] as const;

/**
 * Ends the session when nobody is there, and when the application is closed.
 *
 * Mounted once, above the routes. It renders nothing until the warning is due,
 * and nothing at all for somebody who is not signed in.
 *
 * Everything here is the desktop's: the idle countdown and the beacon on the
 * way out both mean something on a machine in an office and something else
 * entirely on a tablet, where putting the device down and opening the camera
 * are the same gesture as leaving. On a touch device this component does
 * nothing at all, and the session ends the way it does everywhere - when the
 * server's window runs out, or when the application is closed and the browser
 * drops the cookie.
 *
 * The countdown is a warning, not the mechanism. The server decides.
 */
export function SesijaCuvar() {
  const { user, logout } = useAuth();
  const [preostalo, setPreostalo] = useState<number | null>(null);
  const [minuta, setMinuta] = useState(PRETPOSTAVLJENE_MINUTE);
  // Held in a ref as well so the activity listeners, which are attached once,
  // can read the current state without being torn down and rebuilt.
  const upozorava = useRef(false);
  const odgoda = useRef<ReturnType<typeof setTimeout> | null>(null);

  const odjavi = useCallback(() => {
    upozorava.current = false;
    setPreostalo(null);
    void logout();
  }, [logout]);

  const pokreniSat = useCallback(() => {
    if (odgoda.current) clearTimeout(odgoda.current);
    odgoda.current = setTimeout(
      () => {
        upozorava.current = true;
        setPreostalo(ODBROJAVANJE_S);
      },
      minuta * 60 * 1000,
    );
  }, [minuta]);

  // One question, and a failure changes nothing: the fallback is the same
  // number the server ships with, and the server ends the session either way.
  useEffect(() => {
    if (!user) return;
    const kontroler = new AbortController();
    void dohvatiMinuteNeaktivnosti(kontroler.signal)
      .then(setMinuta)
      .catch(() => undefined);
    return () => kontroler.abort();
  }, [user]);

  // The idle clock, and everything that counts as being there.
  //
  // Desktop only. On a phone or a tablet there is no shared machine to lock
  // and no unattended screen to worry about, and an engineer who puts the
  // tablet down to carry something must find his work where he left it. There,
  // the session ends by the server's own window and by closing the app.
  useEffect(() => {
    if (!user || jeDodirniUredaj()) return;

    const naAktivnost = () => {
      // While the warning is up, only the button counts. Otherwise a stray
      // scroll from a bag or a pocket would cancel it silently.
      if (upozorava.current) return;
      pokreniSat();
    };

    pokreniSat();
    for (const dogadaj of DOGADAJI_AKTIVNOSTI) {
      window.addEventListener(dogadaj, naAktivnost, { passive: true });
    }
    const prestani = pratiAktivnost(naAktivnost);

    return () => {
      if (odgoda.current) clearTimeout(odgoda.current);
      for (const dogadaj of DOGADAJI_AKTIVNOSTI) {
        window.removeEventListener(dogadaj, naAktivnost);
      }
      prestani();
    };
  }, [user, pokreniSat]);

  // The countdown itself, running only while the warning is up. The last tick
  // signs out from inside the timeout rather than from the effect body: the
  // effect only ever schedules, it never acts.
  useEffect(() => {
    if (preostalo === null) return;

    const otkucaj = setTimeout(() => {
      if (preostalo <= 1) odjavi();
      else setPreostalo(preostalo - 1);
    }, 1000);
    return () => clearTimeout(otkucaj);
  }, [preostalo, odjavi]);

  // Closing the last window ends the session - on a desktop. On a touch device
  // `pagehide` also fires for switching apps, and signing somebody out for
  // taking a photo of a delivery note is worse than a session that waits for
  // its own window to run out.
  useEffect(() => {
    if (!user || jeDodirniUredaj()) return;

    const { odjaviTab, otkucaj } = registrirajTab(crypto.randomUUID());
    const puls = setInterval(otkucaj, 20_000);

    const naOdlasku = (event: PageTransitionEvent) => {
      // A page going into the back/forward cache is coming back, not leaving.
      if (event.persisted) return;
      if (odjaviTab()) odjaviBeaconom();
    };

    window.addEventListener("pagehide", naOdlasku);
    return () => {
      clearInterval(puls);
      odjaviTab();
      window.removeEventListener("pagehide", naOdlasku);
    };
  }, [user]);

  if (!user || preostalo === null) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="sesija-naslov"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="flex w-full max-w-md flex-col gap-4 rounded-[var(--radius-card)] bg-[var(--color-card)] p-6">
        <h2 id="sesija-naslov" className="text-[var(--text-card)] font-semibold">
          Odjava za {preostalo} s
        </h2>
        <p className="text-[var(--color-muted-foreground)]">
          Dugo niste ništa radili pa vas sustav odjavljuje. Nespremljeni unosi
          ostaju u redu za slanje.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="lg"
            onClick={() => {
              upozorava.current = false;
              setPreostalo(null);
              pokreniSat();
            }}
          >
            Ostani prijavljen
          </Button>
          <Button size="lg" variant="ghost" onClick={odjavi}>
            Odjavi me
          </Button>
        </div>
      </div>
    </div>
  );
}
